/**
 * Holidays and vacation-zones API.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from './api-client';
import type {
  Holiday,
  CreateHolidayDto,
  UpdateHolidayDto,
  VacationZoneRef,
} from '@/types/holiday.types';
import type { PaginatedResponse } from '@/types/data-table.types';
import { toLocalDateString } from '@/utils/format-date-utils';

/** Append query params to a path when the search string is non-empty. */
function listUrl(path: string, searchParams: URLSearchParams): string {
  const q = searchParams.toString();
  return q ? `${path}?${q}` : path;
}

/** Trigger a browser download of an XLSX blob. */
function downloadXlsx(data: Blob, filename: string): void {
  const blob = new Blob([data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
}

/** `GET /academic/holidays` — paginated holidays. */
export const getHolidays = async (params?: {
  page?: number;
  pageSize?: number;
  types?: string[];
  schoolYearId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  search?: string;
  vacationZoneId?: string;
}): Promise<PaginatedResponse<Holiday>> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  params?.types?.forEach((t) => searchParams.append('types', t));
  if (params?.schoolYearId) searchParams.append('schoolYearId', params.schoolYearId);
  if (params?.dateFrom) searchParams.append('dateFrom', toLocalDateString(params.dateFrom));
  if (params?.dateTo) searchParams.append('dateTo', toLocalDateString(params.dateTo));
  if (params?.search) searchParams.append('search', params.search);
  if (params?.vacationZoneId) searchParams.append('vacationZoneId', params.vacationZoneId);
  const { data } = await apiClient.get<PaginatedResponse<Holiday>>(
    listUrl('/academic/holidays', searchParams),
  );
  return data;
};

/** `GET /academic/holidays/:id` */
export const getHoliday = async (id: string): Promise<Holiday> => {
  const { data } = await apiClient.get(`/academic/holidays/${id}`);
  return data;
};

/** `POST /academic/holidays` */
export const createHoliday = async (dto: CreateHolidayDto): Promise<Holiday> => {
  const { data } = await apiClient.post('/academic/holidays', dto);
  return data;
};

/** `PATCH /academic/holidays/:id` */
export const updateHoliday = async (id: string, dto: UpdateHolidayDto): Promise<Holiday> => {
  const { data } = await apiClient.patch(`/academic/holidays/${id}`, dto);
  return data;
};

/** `DELETE /academic/holidays/:id` */
export const deleteHoliday = async (id: string): Promise<void> => {
  await apiClient.delete(`/academic/holidays/${id}`);
};

/** `GET /academic/vacation-zones` */
export const getVacationZones = async (): Promise<VacationZoneRef[]> => {
  const { data } = await apiClient.get<VacationZoneRef[]>('/academic/vacation-zones');
  return data;
};

/** `GET /academic/holidays/export` — download XLSX. */
export const exportHolidaysExcel = async (): Promise<void> => {
  const response = await apiClient.get('/academic/holidays/export', { responseType: 'blob' });
  downloadXlsx(response.data, `holidays-${new Date().toISOString().slice(0, 10)}.xlsx`);
};
